/**
 * The signed-in User's own profile (route `/profile`; Feature: user-and-data-management;
 * Requirements 3.1–3.3, 3.7, 4.1–4.4, 4.6, 8.1).
 *
 * This page exists because the complaint was literal: there was no way to see details about the user or
 * edit anything. Requirement 3.1 says an identity affordance in the header navigates here, and
 * {@link import('../components/UserPanel').UserPanel} — the popover behind the top bar's person glyph —
 * is where that link lives, as "Manage Profile". It was the account switcher's role chip until the rail
 * stopped naming the identity; Requirement 3.1's intent is unchanged, only the affordance satisfying it
 * moved.
 *
 * Built on the `MembersPage` conventions — the same `LoadState` shape, the same `useWrite` runner, the
 * same "a failed write leaves the pre-request view untouched and the page re-reads only after the API
 * confirms" rule. What is specific to this page:
 *
 *  - **Every field renders, including the absent ones.** Requirement 3.2 requires an absent value to be
 *    *stated* rather than rendered blank, so the API sends an explicit `null` and this page says "Not
 *    set". A `??`-collapsed blank cell would satisfy the code and not the requirement.
 *  - **Nothing here is gated on a write role.** A person's own name, password, and personal-data export
 *    are not Account-scoped privileges, so a viewer sees and uses all of it. That is the opposite of
 *    every other write surface in the dashboard and it is correct: `useCanWrite` answers a question about
 *    the *Account*, and none of these controls act on one.
 *  - **The three withheld capabilities are shown, explained, and disabled** rather than omitted (Req 3.4,
 *    3.5, 4.5, 11). An unexplained gap reads as a bug — which is exactly the report that produced this
 *    page — so each carries the API's own one-line reason. The reasons come from the API rather than from
 *    a constant here, so the sentence beside a disabled control and the refusal `PATCH /v1/me` returns
 *    for the same field cannot drift apart.
 *  - **The API-keys-survive-a-password-change note is prominent, not a footnote** (Req 4.6). It sits
 *    beside the password form as an {@link InfoTooltip} *and* as body copy, because the misconception is
 *    held before the change rather than after: a User who changed their password believing it revoked
 *    machine access has been misled by silence.
 *  - **The export downloads as a file**, via a Blob and an object URL, because the whole point of an
 *    Article 15 answer is that the subject keeps it.
 *
 * Per-field errors are the API's, placed by {@link FormField}. Both password failures arrive as a
 * `400 validation_failed`, and which field they name is the requirement: `currentPassword` for a wrong
 * current password with **nothing** said about the new one (Req 4.2), `newPassword` for a policy
 * violation with the failed requirement named (Req 4.3).
 */

import { useEffect, useState } from 'react';

import type { FieldError } from '@soroush/core';

import { ApiError } from '../api/client';
import type { ChangePasswordResponse, ProfileDto, UnavailableFeature } from '../api/types';
import { useApiClient } from '../api/use-api-client';
import { FormErrorPanel, FormField, unmappedFieldErrors } from '../components/FieldErrorText';
import { InfoTooltip } from '../components/InfoTooltip';
import { formatEpochSeconds } from '../lib/format';
import { useWrite } from '../lib/use-write';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string; isAuthError: boolean }
  | { status: 'loaded'; profile: ProfileDto };

/**
 * An absent value, stated (Req 3.2).
 *
 * Takes `string | null` rather than `string | undefined | null` on purpose: the API's contract is an
 * explicit `null`, and accepting `undefined` here would let a field the API never sent render as "Not
 * set" — which looks identical on screen and means something entirely different (a missing key is a
 * client/server disagreement, not an empty profile field).
 */
function orNotSet(value: string | null): string {
  return value !== null && value.length > 0 ? value : 'Not set';
}

export function ProfilePage(): JSX.Element {
  const client = useApiClient();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [notice, setNotice] = useState<string | undefined>(undefined);
  // Three runners, because the three writes fail independently and a failure in one must not clear
  // another's message or disable its controls.
  const rename = useWrite();
  const password = useWrite();
  const download = useWrite();

  // The same effect shape `MembersPage` uses: keyed on the client (which `useApiClient` rebuilds when the
  // selected Account changes, so a switch refetches), guarded by `active` so a late response from an
  // earlier client cannot clobber a newer one. `useResource` is not used because a successful write
  // replaces the profile from its own response, and `useResource` owns the value it loads.
  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    client
      .getProfile()
      .then((response) => {
        if (active) setState({ status: 'loaded', profile: response.profile });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message = error instanceof Error ? error.message : 'Failed to load your profile';
        const isAuthError = error instanceof ApiError && error.isAuthError;
        setState({ status: 'error', message, isAuthError });
      });
    return () => {
      active = false;
    };
  }, [client]);

  if (state.status === 'loading') {
    return (
      <section>
        <h1>Your profile</h1>
        <p role="status" className="muted">
          Loading your profile…
        </p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section>
        <h1>Your profile</h1>
        <div role="alert" className="error-panel">
          <p>
            {state.isAuthError
              ? 'Your session expired. Please sign in again.'
              : 'Could not load your profile.'}
          </p>
          <p className="muted">{state.message}</p>
        </div>
      </section>
    );
  }

  const { profile } = state;

  return (
    <section>
      <div className="section-header">
        <div className="section-action-with-hint">
          <h1>Your profile</h1>
          <InfoTooltip label="About your profile">
            Your name, email address, password, and multi-factor settings are held by the platform&apos;s
            identity provider, not copied into the platform&apos;s own stores.
          </InfoTooltip>
        </div>
      </div>

      {notice !== undefined ? (
        <p role="status" className="muted">
          {notice}
        </p>
      ) : null}

      <IdentityDetails profile={profile} />

      <h2>Display name</h2>
      <NameForm
        profile={profile}
        submitting={rename.state.submitting}
        fieldErrors={rename.state.fieldErrors}
        errorMessage={rename.state.errorMessage}
        onSubmit={(name) =>
          void rename.run(
            () => client.updateProfile({ name }),
            (response) => {
              // The API returns the whole profile, so the page replaces its state from one shape rather
              // than patching a field locally — which is what keeps the view and the server in step after
              // a write, and is why nothing is mutated before the API confirms.
              setState({ status: 'loaded', profile: response.profile });
              setNotice(`Your display name is now ${response.profile.name ?? 'not set'}.`);
            },
          )
        }
      />

      <h2>Password</h2>
      <PasswordForm
        submitting={password.state.submitting}
        fieldErrors={password.state.fieldErrors}
        errorMessage={password.state.errorMessage}
        onSubmit={(payload) =>
          void password.run(
            () => client.changePassword(payload),
            (response: ChangePasswordResponse) => {
              // The API's own sentence, not a paraphrase: it is the one that states both Requirement 4.4
              // (you are still signed in) and Requirement 4.6 (your API keys keep working).
              setNotice(response.message);
            },
          )
        }
      />

      <h2>Your data</h2>
      <PersonalDataExport
        submitting={download.state.submitting}
        errorMessage={download.state.errorMessage}
        onDownload={() =>
          void download.run(
            () => client.exportPersonalData(),
            (response) => {
              downloadJson(response.export, `soroush-personal-data-${profile.userSub}.json`);
              setNotice('Your personal data has been downloaded.');
            },
          )
        }
      />

      <h2>Not available yet</h2>
      <UnavailableList entries={profile.unavailable} />
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* The identity details (Req 3.2)                                              */
/* -------------------------------------------------------------------------- */

function IdentityDetails({ profile }: { profile: ProfileDto }): JSX.Element {
  return (
    <>
      <dl className="detail-list" aria-label="Identity">
        <div className="detail-row">
          <dt>Name</dt>
          {/* Req 3.2 — an absent value is stated, not rendered blank. */}
          <dd data-testid="profile-name">{orNotSet(profile.name)}</dd>
        </div>
        <div className="detail-row">
          <dt>Email</dt>
          <dd data-testid="profile-email">
            {orNotSet(profile.email)}
            {profile.email !== null ? (
              <span className="muted">
                {profile.emailVerified ? ' (verified)' : ' (not verified)'}
              </span>
            ) : null}
          </dd>
        </div>
        <div className="detail-row">
          <dt>Multi-factor authentication</dt>
          <dd data-testid="profile-mfa">
            {profile.mfaEnabled
              ? `On (${profile.mfaMethods.join(', ')})`
              : 'Off'}
          </dd>
        </div>
        <div className="detail-row">
          <dt>Account created</dt>
          <dd data-testid="profile-created">
            {profile.createdAtEpoch === null
              ? 'Not recorded'
              : formatEpochSeconds(profile.createdAtEpoch)}
          </dd>
        </div>
        <div className="detail-row">
          <dt>User id</dt>
          <dd>
            {/* The opaque identifier the members table shows and audit records reference (Req 2.3), so a
                User can recognise their own row there. */}
            <code>{profile.userSub}</code>
          </dd>
        </div>
      </dl>

      <h2>Accounts</h2>
      {profile.memberships.length === 0 ? (
        <p className="muted">You are not a member of any account.</p>
      ) : (
        <table className="checks-table" aria-label="Your accounts">
          <thead>
            <tr>
              <th scope="col">Account</th>
              <th scope="col">Your role</th>
            </tr>
          </thead>
          <tbody>
            {profile.memberships.map((account) => (
              <tr key={account.accountId}>
                <td>
                  <code>{account.accountId}</code>
                </td>
                {/* The role in THAT account, which for a shared one comes from the membership rather
                    than from the token — the same person can be admin in one and viewer in another. */}
                <td>{account.role}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted">
        Your role and which accounts you belong to are set by an account administrator on the members
        page. You cannot change either here.
      </p>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Rename (Req 3.3, 3.7)                                                       */
/* -------------------------------------------------------------------------- */

/** The path the API roots a `PATCH /v1/me` field error at — the attribute name itself. */
const NAME_PATH = 'name';

function NameForm({
  profile,
  submitting,
  fieldErrors,
  errorMessage,
  onSubmit,
}: {
  profile: ProfileDto;
  submitting: boolean;
  fieldErrors?: readonly FieldError[];
  errorMessage?: string;
  onSubmit: (name: string) => void;
}): JSX.Element {
  const [name, setName] = useState(profile.name ?? '');

  return (
    <form
      className="entity-form"
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault();
        onSubmit(name);
      }}
    >
      <FormErrorPanel
        message={errorMessage}
        unmapped={unmappedFieldErrors(fieldErrors, [NAME_PATH])}
      />

      <FormField
        label="Display name"
        path={NAME_PATH}
        errors={fieldErrors}
        hint="Shown to you here. The platform identifies you to other members by your user id, not your name."
        render={(props) => (
          <input
            {...props}
            type="text"
            value={name}
            disabled={submitting}
            onChange={(changeEvent) => setName(changeEvent.target.value)}
          />
        )}
      />

      <div className="form-actions">
        <button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save name'}
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* Password (Req 4.1–4.4, 4.6)                                                 */
/* -------------------------------------------------------------------------- */

const CURRENT_PASSWORD_PATH = 'currentPassword';
const NEW_PASSWORD_PATH = 'newPassword';

function PasswordForm({
  submitting,
  fieldErrors,
  errorMessage,
  onSubmit,
}: {
  submitting: boolean;
  fieldErrors?: readonly FieldError[];
  errorMessage?: string;
  onSubmit: (payload: { currentPassword: string; newPassword: string }) => void;
}): JSX.Element {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  return (
    <form
      className="entity-form"
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault();
        onSubmit({ currentPassword, newPassword });
      }}
    >
      {/*
        Req 4.6, stated before the change rather than after. A User who changes their password believing
        it revoked machine access has been misled, so this is body copy in the form rather than a line in
        the success message — by the time the success message appears the decision has been made.
      */}
      <div role="note" className="adoption-notice" data-testid="api-keys-note">
        <p>
          Changing your password does <strong>not</strong> revoke your API keys. An API key is a
          separate credential with its own lifecycle; revoke one from the API keys page if that is what
          you mean to do.
        </p>
        <p className="muted">Your current session stays signed in after a password change.</p>
      </div>

      <FormErrorPanel
        message={errorMessage}
        unmapped={unmappedFieldErrors(fieldErrors, [CURRENT_PASSWORD_PATH, NEW_PASSWORD_PATH])}
      />

      <FormField
        label="Current password"
        path={CURRENT_PASSWORD_PATH}
        errors={fieldErrors}
        render={(props) => (
          <input
            {...props}
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            disabled={submitting}
            onChange={(changeEvent) => setCurrentPassword(changeEvent.target.value)}
          />
        )}
      />

      <FormField
        label="New password"
        path={NEW_PASSWORD_PATH}
        errors={fieldErrors}
        // No local policy hint listing the rules, deliberately: the pool's policy is the authority and a
        // second copy here would be the one that goes stale. When a password is refused the API names the
        // requirement that failed (Req 4.3) and it lands right here.
        hint={
          <>
            Must meet this account&apos;s password policy.{' '}
            <InfoTooltip label="About your API keys and your password">
              Your API keys are unaffected by a password change, and you stay signed in.
            </InfoTooltip>
          </>
        }
        render={(props) => (
          <input
            {...props}
            type="password"
            autoComplete="new-password"
            value={newPassword}
            disabled={submitting}
            onChange={(changeEvent) => setNewPassword(changeEvent.target.value)}
          />
        )}
      />

      <div className="form-actions">
        <button type="submit" disabled={submitting}>
          {submitting ? 'Changing…' : 'Change password'}
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* Export (Req 8.1)                                                            */
/* -------------------------------------------------------------------------- */

function PersonalDataExport({
  submitting,
  errorMessage,
  onDownload,
}: {
  submitting: boolean;
  errorMessage?: string;
  onDownload: () => void;
}): JSX.Element {
  return (
    <div className="card">
      <p>
        Download the personal data the platform holds about <strong>you</strong>: your identity as held
        by the identity provider, and the accounts you can act as.
      </p>
      {/*
        The two exports answer different rights and have different contents, so the distinction is drawn
        here rather than left for a User to discover from a shorter-than-expected file. The account export
        (Requirement 8.2) is not built.
      */}
      <p className="muted">
        This is not an export of an account&apos;s monitoring data — checks, results, captured response
        bodies, and traces belong to the account rather than to you, and exporting them is a separate
        operation that is not available yet.
      </p>
      <FormErrorPanel message={errorMessage} />
      <div className="form-actions">
        {/*
          Not gated on a write role: reading your own data is a read. `useCanWrite` answers a question
          about the account, and this control does not act on one.
        */}
        <button type="button" disabled={submitting} onClick={onDownload}>
          {submitting ? 'Preparing…' : 'Download my data (JSON)'}
        </button>
      </div>
    </div>
  );
}

/**
 * Save `data` as a JSON file the User keeps.
 *
 * A Blob and an object URL rather than a link straight to the API route, because the route needs the
 * bearer token: a plain `<a href>` would be an unauthenticated navigation and would return `401`. The URL
 * is revoked immediately after the click — an un-revoked object URL keeps the whole export alive in memory
 * for the life of the document, and this one is personal data.
 */
function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/* -------------------------------------------------------------------------- */
/* The withheld capabilities (Req 3.4, 3.5, 4.5, 11)                           */
/* -------------------------------------------------------------------------- */

/**
 * Every capability the platform does not offer yet, shown as **explained and unavailable** (Req 3.4, 3.5,
 * 4.5, 11).
 *
 * Rendered rather than omitted, because an unexplained gap reads as a bug — which is the report that
 * produced this page. Each control is genuinely disabled and carries the API's own reason, so the
 * sentence a User reads beside a greyed-out button and the refusal the API returns for the same field are
 * the same text.
 */
function UnavailableList({ entries }: { entries: readonly UnavailableFeature[] }): JSX.Element {
  if (entries.length === 0) {
    return <p className="muted">Everything on this page is available.</p>;
  }
  return (
    <div className="card" data-testid="unavailable-list">
      {entries.map((entry) => (
        <div key={entry.feature} className="unavailable-feature" data-unavailable={entry.feature}>
          <h3>{entry.label}</h3>
          <p className="muted">{entry.reason}</p>
          {/*
            A real, disabled control rather than prose alone. The affordance is what a User looks for, so
            showing it disabled answers "where is it?" in the place they looked; `aria-disabled` alongside
            `disabled` so a screen reader announces the state rather than skipping the element.
          */}
          <button type="button" disabled aria-disabled="true">
            {entry.label}
          </button>
        </div>
      ))}
    </div>
  );
}
